import React, { useEffect, useRef } from 'react';
import styles from './style.module.css';

export interface AutocompleteOption {
  value: string;
  label: string;
}

export interface AutocompleteProps {
  label: string;
  value: AutocompleteOption | null;
  setValue: (option: AutocompleteOption | null) => void;
  options: AutocompleteOption[];
  icon?: React.ReactNode;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
  label,
  value,
  setValue,
  options,
  icon,
  isOpen,
  setIsOpen,
  searchQuery,
  setSearchQuery,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleAutocompleteOptionClick = (option: AutocompleteOption) => {
    setValue(option);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(!isOpen);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const filteredAutocompleteOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen]);

  return (
    <div className={styles.formControl} ref={wrapperRef}>
      <label className={styles.label}>{label}</label>
      <div
        aria-controls="dropdown"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        tabIndex={0}
        className={`${styles.wrapSelect} ${isOpen ? styles.openselect : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
      >
        {icon}
        <div className={styles.selectedValue}>
          {value?.label || 'Selecione...'}
        </div>
        <div className={styles.arrowIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6e6e73"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {isOpen && (
        <div
          role="listbox"
          className={`${styles.dropdown} ${isOpen ? styles.open : ''}`}
        >
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Buscar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            ref={inputRef}
          />
          {filteredAutocompleteOptions.length > 0 ? (
            filteredAutocompleteOptions.map((option) => (
              <div
                key={option.value}
                aria-selected={value?.value === option.value}
                role="option"
                tabIndex={0}
                className={styles.option}
                onClick={() => handleAutocompleteOptionClick(option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleAutocompleteOptionClick(option);
                  }
                }}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className={styles.noResults}>Nenhum resultado encontrado</div>
          )}
        </div>
      )}
    </div>
  );
};
